(() => {
  const NS = '[BedConfigurator]';

  /* I18N-06 (#163): visible strings come from the page; the English literal is the fallback. */
  const wfStrings = (() => {
    try {
      const node = document.querySelector('script[type="application/json"][data-wf-i18n="bed-configurator"]');
      const parsed = node ? JSON.parse(node.textContent) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  })();
  // Shopify's `t` HTML-escapes every translation whose key does not end in `_html` (French
  // "n'a" arrives as "n&#39;a"), so the page's values are decoded back to plain text.
  const wfDecode = (text) =>
    text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (entity, body) => {
      const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }[body.toLowerCase()];
      if (named) return named;
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    });
  const tr = (key, fallback, values = {}) => {
    const text = typeof wfStrings[key] === 'string' && wfStrings[key] !== '' ? wfDecode(wfStrings[key]) : fallback;
    return Object.keys(values).reduce((out, name) => out.split(`{${name}}`).join(values[name]), text);
  };

  function normalize(value) {
    return String(value || '').trim();
  }

  function normalizeKey(value) {
    return normalize(value).toLowerCase();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function sizeKey(size) {
    const key = normalizeKey(size);

    if (key === 'small' || key === 's') return 'small';
    if (key === 'medium' || key === 'm') return 'medium';
    if (key === 'large' || key === 'l') return 'large';
    if (key === 'extra large' || key === 'xl' || key === 'x-large') return 'extra_large';

    return '';
  }

  function sizeBadge(size) {
    const key = normalizeKey(size);

    if (key === 'small' || key === 's') return 'S';
    if (key === 'medium' || key === 'm') return 'M';
    if (key === 'large' || key === 'l') return 'L';
    if (key === 'extra large' || key === 'xl' || key === 'x-large') return 'XL';

    return normalize(size);
  }

  function colorSwatch(color) {
    const key = normalizeKey(color);

    const map = {
      black: '#000000',
      white: '#ffffff',
      grey: '#8e8e93',
      gray: '#8e8e93',
      cream: '#d8c3a5',
      brown: '#8d6e63',
      beige: '#e8d7a6',
      'pale pink': '#f7bfd2',
      pink: '#e75a8b',
    };

    return map[key] || '#d0cfd2';
  }

  function rangeOrder(range) {
    const key = normalizeKey(range);

    if (key === 'premium') return 1;
    if (key === 'corduroy') return 2;
    if (key === 'fluffy') return 3;
    if (key === 'hammock') return 4;

    return 99;
  }

  function sizeOrder(size) {
    const key = normalizeKey(size);

    if (key === 'small' || key === 's') return 1;
    if (key === 'medium' || key === 'm') return 2;
    if (key === 'large' || key === 'l') return 3;
    if (key === 'extra large' || key === 'xl' || key === 'x-large') return 4;

    return 99;
  }

  function shapeIcon(section, shape) {
    const key = normalizeKey(shape);

    const imgUrl =
      key === 'round'
        ? section.dataset.shapeRoundImage
        : section.dataset.shapeRectangleImage;

    if (!imgUrl) {
      return `<span class="bed-configurator__shape-label">${escapeHtml(shape)}</span>`;
    }

    return `
      <span class="bed-configurator__shape-icon" aria-hidden="true">
        <img src="${escapeHtml(imgUrl)}" alt="" loading="lazy">
      </span>
      <span class="bed-configurator__shape-label">${escapeHtml(shape)}</span>
    `;
  }

  function getMatchingVariant(product, state) {
    if (!Array.isArray(product.variants)) return null;

    return product.variants.find((variant) => {
      const options = variant.options || [];

      const sizeMatch =
        !state.size ||
        options.some((option) => normalizeKey(option) === normalizeKey(state.size));

      const colorMatch =
        !state.color ||
        options.some((option) => normalizeKey(option) === normalizeKey(state.color));

      return sizeMatch && colorMatch;
    }) || product.variants[0] || null;
  }

  function buildCard(product, state) {
    const variant = getMatchingVariant(product, state);
    const url = variant?.url || product.url;
    const image = variant?.image || product.image;
    const price = variant?.price || '';
    const shape = (product.shapes || [])[0] || '';
    const range = product.range || '';

    return `
      <article class="bed-configurator-card">
        <a href="${escapeHtml(url)}" class="bed-configurator-card__media">
          ${
            image
              ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
              : `<div class="bed-configurator-card__placeholder"></div>`
          }
        </a>

        <div class="bed-configurator-card__body">
          <a href="${escapeHtml(url)}" class="bed-configurator-card__title">
            ${escapeHtml(product.title)}
          </a>

          <div class="bed-configurator-card__meta">
            ${range ? `${escapeHtml(range)} / ` : ''}
            ${escapeHtml(shape)}
            ${state.size ? ` / ${escapeHtml(state.size)}` : ''}
            ${state.color ? ` / ${escapeHtml(state.color)}` : ''}
          </div>

          ${price ? `<div class="bed-configurator-card__price">${escapeHtml(price)}</div>` : ''}

          <div class="bed-configurator-card__cta">
            <a href="${escapeHtml(url)}" class="bed-configurator-card__btn">
              ${/* I18N-06 (#163): plain text; the bridge test keeps < > " out of every value. */ tr('view_bed', 'View bed')}
            </a>
          </div>
        </div>
      </article>
    `;
  }

  function unique(values) {
    return [...new Set(values.map(normalize).filter(Boolean))];
  }

  function renderOptions(host, values, type, selected) {
    if (!host) return;

    host.innerHTML = values
      .map((value) => {
        const active = normalizeKey(value) === normalizeKey(selected);

        if (type === 'range') {
          return `
            <button
              type="button"
              class="bed-configurator__option bed-configurator__option--range ${active ? 'is-active' : ''}"
              data-filter="${type}"
              data-value="${escapeHtml(value)}"
              aria-label="${escapeHtml(value)}"
            >
              <span>${escapeHtml(value)}</span>
            </button>
          `;
        }

        if (type === 'shape') {
          return `
            <button
              type="button"
              class="bed-configurator__option bed-configurator__option--shape ${active ? 'is-active' : ''}"
              data-filter="${type}"
              data-value="${escapeHtml(value)}"
              aria-label="${escapeHtml(value)}"
            >
              ${shapeIcon(host.closest('s-bed-configurator'), value)}
            </button>
          `;
        }

        if (type === 'size') {
          return `
            <button
              type="button"
              class="bed-configurator__option bed-configurator__option--size ${active ? 'is-active' : ''}"
              data-filter="${type}"
              data-value="${escapeHtml(value)}"
              aria-label="${escapeHtml(value)}"
            >
              <span class="bed-configurator__size-badge">${escapeHtml(sizeBadge(value))}</span>
              <span>${escapeHtml(value)}</span>
            </button>
          `;
        }

        return `
          <button
            type="button"
            class="bed-configurator__option bed-configurator__option--color ${active ? 'is-active' : ''}"
            data-filter="${type}"
            data-value="${escapeHtml(value)}"
            aria-label="${escapeHtml(value)}"
          >
            <span
              class="bed-configurator__color-swatch"
              style="--swatch:${escapeHtml(colorSwatch(value))}"
            ></span>
            <span>${escapeHtml(value)}</span>
          </button>
        `;
      })
      .join('');
  }

  async function loadBreeds(section) {
    const url = section.dataset.breedsUrl;
    if (!url) return {};

    try {
      const res = await fetch(url, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      console.warn(NS, 'Could not load breeds JSON', err);
      return {};
    }
  }

  function renderBreeds(section, breeds, selectedSize) {
    const host = section.querySelector('[data-breed-list]');
    if (!host) return;

    const key = sizeKey(selectedSize);
    const list = key && Array.isArray(breeds[key]) ? breeds[key] : [];

    if (!list.length) {
      /* I18N-06 (#163): the prompt comes from the section's bridge, as plain text. */
      host.innerHTML = `<li>${tr('breeds_empty', 'Select a size to see typical breeds.')}</li>`;
      return;
    }

    host.innerHTML = list.map((breed) => `<li>${escapeHtml(breed)}</li>`).join('');
  }

  function init(section) {
    const jsonEl = section.querySelector('[data-bed-products]');
    if (!jsonEl) return;

    let products = [];

    try {
      products = JSON.parse(jsonEl.textContent || '[]').filter((product) => product && product.title);
    } catch (err) {
      console.error(NS, 'Invalid embedded product JSON', err);
      return;
    }

    const state = {
      range: '',
      shape: '',
      size: '',
      color: '',
    };

    const rangeHost = section.querySelector('[data-filter-group="range"]');
    const shapeHost = section.querySelector('[data-filter-group="shape"]');
    const sizeHost = section.querySelector('[data-filter-group="size"]');
    const colorHost = section.querySelector('[data-filter-group="color"]');
    const countHost = section.querySelector('[data-results-count]');
    const resultsHost = section.querySelector('[data-results-grid]');
    const prevBtn = section.querySelector('[data-slider-prev]');
    const nextBtn = section.querySelector('[data-slider-next]');

    let breeds = {};

    function getFilteredProducts() {
      return products.filter((product) => {
        const rangeMatch =
          !state.range ||
          normalizeKey(product.range) === normalizeKey(state.range);

        const shapeMatch =
          !state.shape ||
          (product.shapes || []).some((shape) => normalizeKey(shape) === normalizeKey(state.shape));

        const sizeMatch =
          !state.size ||
          (product.sizes || []).some((size) => normalizeKey(size) === normalizeKey(state.size));

        const colorMatch =
          !state.color ||
          (product.colors || []).some((color) => normalizeKey(color) === normalizeKey(state.color));

        return rangeMatch && shapeMatch && sizeMatch && colorMatch;
      });
    }

    function bindFilterClicks() {
      section.querySelectorAll('[data-filter]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const type = btn.dataset.filter;
          const value = btn.dataset.value;

          state[type] =
            normalizeKey(state[type]) === normalizeKey(value)
              ? ''
              : value;

          render();
        });
      });
    }

    function render() {
      const ranges = unique(products.map((product) => product.range))
        .sort((a, b) => rangeOrder(a) - rangeOrder(b));

      const shapes = unique(products.flatMap((product) => product.shapes || []));

      const sizes = unique(
        products
          .flatMap((product) => product.sizes || [])
          .filter((size) => {
            const key = normalizeKey(size);
            return key !== 'xs' && key !== 'extra small' && key !== 'x-small';
          })
      ).sort((a, b) => sizeOrder(a) - sizeOrder(b));

      const colors = unique(products.flatMap((product) => product.colors || []));

      renderOptions(rangeHost, ranges, 'range', state.range);
      renderOptions(shapeHost, shapes, 'shape', state.shape);
      renderOptions(sizeHost, sizes, 'size', state.size);
      renderOptions(colorHost, colors, 'color', state.color);

      const filteredProducts = getFilteredProducts().sort((a, b) =>
        String(a.title || '').localeCompare(String(b.title || ''), undefined, {
          sensitivity: 'base',
        })
      );

      /* I18N-06 (#163): the count and the empty state come from the section's bridge. */
      if (countHost) {
        countHost.textContent =
          filteredProducts.length === 1
            ? tr('results_count_one', '1 matching bed')
            : tr('results_count', '{number} matching beds', { number: filteredProducts.length });
      }

      if (resultsHost) {
        resultsHost.innerHTML = filteredProducts.length
          ? filteredProducts.map((product) => buildCard(product, state)).join('')
          : `<div class="bed-configurator__empty">${tr('no_results', 'No beds match this combination.')}</div>`;

        resultsHost.scrollTo({ left: 0, behavior: 'smooth' });
      }

      renderBreeds(section, breeds, state.size);
      bindFilterClicks();
    }

    prevBtn?.addEventListener('click', () => {
      resultsHost?.scrollBy({ left: -280, behavior: 'smooth' });
    });

    nextBtn?.addEventListener('click', () => {
      resultsHost?.scrollBy({ left: 280, behavior: 'smooth' });
    });

    loadBreeds(section).then((loadedBreeds) => {
      breeds = loadedBreeds || {};
      render();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('s-bed-configurator').forEach(init);
  });
})();
